/**
 * Open-Meteo weather data, fetched through the worker's `/api/weather`
 * proxy. We don't call Open-Meteo directly from the browser because
 * Safari intermittently surfaces CORS errors when Open-Meteo's edge
 * returns 5xx (the 5xx response is missing CORS headers, so the
 * browser swallows the real status with "blocked by CORS"). The
 * worker proxy gives us consistent CORS handling, edge caching, and
 * a single place to add retries if we want them later.
 *
 * https://open-meteo.com/en/docs
 */

import { API_BASE } from './apiBase';

export interface DayWeather {
  /** ISO date string YYYY-MM-DD */
  date: string;
  tempHighF: number;
  tempLowF: number;
  /** 0–100 */
  precipitationProbability: number;
  /** WMO weather interpretation code */
  weatherCode: number;
  /** Human-readable condition label */
  condition: string;
  /** Emoji representing the condition */
  conditionEmoji: string;
}

/** WMO weather interpretation codes → label + emoji */
const wmoCondition = (code: number): { condition: string; conditionEmoji: string } => {
  if (code === 0) return { condition: 'Clear', conditionEmoji: '☀️' };
  if (code <= 2) return { condition: 'Partly cloudy', conditionEmoji: '⛅' };
  if (code === 3) return { condition: 'Overcast', conditionEmoji: '☁️' };
  if (code <= 49) return { condition: 'Foggy', conditionEmoji: '🌫️' };
  if (code <= 59) return { condition: 'Drizzle', conditionEmoji: '🌦️' };
  if (code <= 69) return { condition: 'Rain', conditionEmoji: '🌧️' };
  if (code <= 79) return { condition: 'Snow', conditionEmoji: '❄️' };
  if (code <= 82) return { condition: 'Rain showers', conditionEmoji: '🌦️' };
  if (code <= 86) return { condition: 'Snow showers', conditionEmoji: '🌨️' };
  if (code <= 99) return { condition: 'Thunderstorm', conditionEmoji: '⛈️' };
  return { condition: 'Unknown', conditionEmoji: '🌡️' };
};

interface OpenMeteoResponse {
  daily: {
    time: Array<string>;
    temperature_2m_max: Array<number>;
    temperature_2m_min: Array<number>;
    precipitation_probability_max: Array<number>;
    weather_code: Array<number>;
  };
}

/**
 * Fetch daily weather forecasts for a lat/lng over a date range.
 * Open-Meteo only provides forecasts up to ~16 days out; past dates are returned from
 * the ERA5 historical archive (seamlessly handled by the API).
 */
export const fetchWeather = async (
  lat: number,
  lng: number,
  startDate: string,
  endDate: string,
): Promise<Array<DayWeather>> => {
  // The worker proxy expects just lat/lng/dates — it adds the daily
  // field list, temperature unit, and timezone server-side.
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lng),
    start_date: startDate,
    end_date: endDate,
  });

  const response = await fetch(`${API_BASE}/api/weather?${params.toString()}`);
  if (!response.ok) throw new Error(`Weather proxy error: ${response.status}`);

  const data = (await response.json()) as OpenMeteoResponse;
  const {
    time,
    temperature_2m_max,
    temperature_2m_min,
    precipitation_probability_max,
    weather_code,
  } = data.daily;

  return time.map((date, i) => ({
    date,
    tempHighF: Math.round(temperature_2m_max[i] ?? 0),
    tempLowF: Math.round(temperature_2m_min[i] ?? 0),
    precipitationProbability: Math.round(precipitation_probability_max[i] ?? 0),
    weatherCode: weather_code[i] ?? 0,
    ...wmoCondition(weather_code[i] ?? 0),
  }));
};
